/**
 * Shared field-level schemas.
 *
 * These are the vocabulary the rest of the contracts are written in. Anything
 * that shows up in more than one request belongs here, so a rule like "a
 * nickname is 1–80 characters" is stated once and can never drift between the
 * create route and the update route.
 */

import { z } from 'zod';

import { isIsoDate } from '@svcdue/core';

/** Every id in the system is a client-generated UUID (§2, DSQL implications). */
export const uuid = z.string().uuid();

/**
 * A calendar date, `YYYY-MM-DD`. Validated with the engine's own parser rather
 * than a regex, so the API can never accept a date the projection engine would
 * then throw on (`2026-02-30` matches any sane regex and is not a real day).
 */
export const isoDate = z.string().refine(isIsoDate, {
  message: 'Expected a real calendar date in YYYY-MM-DD form',
});

/** Trimmed, non-empty, and length-capped — the default for any user-supplied label. */
export const shortText = (max = 80) => z.string().trim().min(1).max(max);

export const optionalText = (max = 80) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

/**
 * A link the UI will render as a clickable "reorder" button, so the scheme is
 * restricted: `javascript:` and `data:` URLs must never reach an href.
 */
export const httpUrl = z
  .string()
  .trim()
  .url()
  .max(2000)
  .refine((v) => /^https?:\/\//i.test(v), { message: 'Only http(s) links are allowed' });

export const optionalHttpUrl = httpUrl.nullable().optional();

/** Non-negative whole number — passes, miles, odometer readings. */
export const count = z.number().int().min(0);

/** A trigger interval. Zero would never converge, so the floor is 1 (see the engine). */
export const intervalValue = z.number().int().min(1).max(1_000_000);

export const pagination = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().optional(),
});

export type Pagination = z.infer<typeof pagination>;
